"use client";

import React, { createContext, useCallback, useContext, useState } from "react";

import { VoiceId } from "@/data/voices";

import { usePlaygroundState } from "./use-playground-state";

import type { PlaygroundState } from "@/data/playground-state";

export type ConnectFn = () => Promise<void>;

interface TokenGeneratorData {
	shouldConnect: boolean;
	wsUrl: string;
	token: string;
	pgState: PlaygroundState;
	voice: VoiceId;
	disconnect: () => Promise<void>;
	connect: ConnectFn;
}

const ConnectionContext = createContext<TokenGeneratorData | undefined>(
	undefined,
);

export const ConnectionProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [connectionDetails, setConnectionDetails] = useState<{
		wsUrl: string;
		token: string;
		shouldConnect: boolean;
		voice: VoiceId;
	}>({ wsUrl: "", token: "", shouldConnect: false, voice: VoiceId.alloy });

	const { pgState, dispatch } = usePlaygroundState();

	const connect = async () => {
		// Remove the check for OpenAI API key since we're using server-side key
		// if (!pgState.openaiAPIKey) {
		//   throw new Error("OpenAI API key is required to connect");
		// }

		const response = await fetch("/api/token", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(pgState),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.error || "Failed to fetch token");
		}

		const { accessToken, url } = await response.json();

		setConnectionDetails({
			wsUrl: url,
			token: accessToken,
			shouldConnect: true,
			voice: pgState.sessionConfig.voice,
		});
	};

	const disconnect = useCallback(async () => {
		setConnectionDetails((prev) => ({ ...prev, shouldConnect: false }));
	}, []);

	// We can simplify this effect since we don't rely on client-side API key now
	// useEffect(() => {
	//   if (pgState.openaiAPIKey === null && connectionDetails.shouldConnect) {
	//     disconnect();
	//   }
	// }, [pgState.openaiAPIKey, connectionDetails.shouldConnect, disconnect]);

	return (
		<ConnectionContext.Provider
			value={{
				wsUrl: connectionDetails.wsUrl,
				token: connectionDetails.token,
				shouldConnect: connectionDetails.shouldConnect,
				voice: connectionDetails.voice,
				pgState,
				connect,
				disconnect,
			}}
		>
			{children}
		</ConnectionContext.Provider>
	);
};

export const useConnection = () => {
	const context = useContext(ConnectionContext);

	if (context === undefined) {
		throw new Error("useConnection must be used within a ConnectionProvider");
	}

	return context;
};
